import { Box, Card, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
// v1 layouts store CSS as element props (`height`, `textTransform`, …); MUI v9 Box/Typography no longer
// read system props, so fold them into `sx` the way MUI v7 did internally.
import { unstable_extendSxProp as extendSxProp, type SxProps } from '@mui/system';
import parse, { Element } from 'html-react-parser';
import Mustache from 'mustache';
import { useMemo } from 'react';
import { PalletTagLayout } from '../types';

import Barcode from './BarCode';
import { ScopedStyleContainer } from './ScopedStyleContainer';

export const basicTemplate: PalletTagLayout = {
  name: 'Basic 4x2',
  width: '4in',
  height: '2.125in',
  version: 1,
  layout: [
    {
      type: 'box',
      width: '100%',
      height: '100%',
      children: [
        {
          name: 'Table',
          type: 'table',
          showDividers: true,
          children: [
            {
              name: 'Header Row',
              type: 'headerRow',
              children: [
                { headerName: 'Lot' },
                { headerName: 'Receive Date' },
                { headerName: 'COO' },
                { headerName: 'Qty' },
              ],
            },
            {
              name: 'Row',
              type: 'row',
              children: [
                { type: 'string', value: '{{ lot }}' },
                { type: 'date', value: '{{{ receiveDate }}}' },
                { type: 'string', value: '{{ countryOfOrigin }}' },
                { type: 'number', value: '{{ quantity }}' },
              ],
            },
            {
              name: 'Header Row (2)',
              type: 'headerRow',
              children: [{ headerName: 'Commodity', colSpan: 2 }, { headerName: 'Pack' }, { headerName: 'Grade' }],
            },
            {
              name: 'Row (2)',
              type: 'row',
              children: [
                { type: 'string', value: '{{{ description }}}', colSpan: 2 },
                { type: 'string', value: '{{{ packStyle }}}' },
                { type: 'string', value: '{{{ grade }}}' },
              ],
            },
          ],
        },
        {
          name: 'Pallet Tag',
          type: 'barcode',
          text: 'Pallet Tag #{{ tagNumber }}',
          width: '60%',
          height: '80%',
          scale: 3,
          barCodeHeight: 5,
          textsize: 5.5,
          inkspread: 0.5,
          value: '{{ tagNumber }}',
        },
      ],
    },
  ],
};

const getWrapWidth = (width) => {
  if (!width) {
    return {};
  }

  return {
    maxWidth: width === 'none' ? undefined : width,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };
};

const renderTable = (table, data) => {
  const rows = table.children.map((row, index) => {
    if (row.type === 'headerRow') {
      return (
        <TableHead key={index}>
          <TableRow>
            {row.children.map((cell, cellIndex) => (
              <TableCell
                key={cellIndex}
                colSpan={cell.colSpan}
                sx={{
                  px: 0.125,
                  py: 0.25,
                  fontWeight: 'bold',
                  ...(table.headerCellStyle || {}),
                  ...(cell.style || {}),
                }}
              >
                {cell.headerName}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
      );
    }

    return (
      <TableBody key={index}>
        <TableRow>
          {row.children.map((cell, cellIndex) => {
            const isSpecial = cell.type === 'box' || cell.type === 'barcode';

            return (
              <TableCell
                key={cellIndex}
                colSpan={cell.colSpan}
                sx={{
                  px: 0.125,
                  py: 0.25,
                  ...getWrapWidth(cell.wrapWidth),
                  ...(table.cellStyle || {}),
                  ...(cell.style || {}),
                }}
              >
                {isSpecial ? renderComponents[cell.type](cell, data) : Mustache.render(cell.value, data)}
              </TableCell>
            );
          })}
        </TableRow>
      </TableBody>
    );
  });

  return <Table>{rows}</Table>;
};

/** Palette names Typography resolves through its own `color` prop; MUI v7 kept these out of `sx`, so do we. */
const TYPOGRAPHY_PALETTE_COLORS = new Set([
  'primary',
  'secondary',
  'error',
  'info',
  'success',
  'warning',
  'textPrimary',
  'textSecondary',
  'textDisabled',
]);

const typographyProps = ({ color, ...element }: { color?: string; sx?: SxProps } & Record<string, unknown>) =>
  color && TYPOGRAPHY_PALETTE_COLORS.has(color)
    ? { ...extendSxProp(element), color }
    : extendSxProp({ ...element, color });

const renderComponents = {
  table: renderTable,
  box: ({ children, ...element }, data) => <Box {...extendSxProp(element)}>{renderElements(children, data)}</Box>,
  value: ({ value }, data) => Mustache.render(value, data),
  text: (element, data) => (
    <Typography {...typographyProps(element)}>{Mustache.render(element.text ?? '', data)}</Typography>
  ),
  barcode: (element, data) => {
    const { lot, gtinCode, rawDate, productCode } = data ?? {};

    return (
      <Barcode
        {...element}
        textTemplate={element.text}
        lotNumber={lot}
        gtinCode={gtinCode}
        packDate={rawDate}
        productGS1={data.customProductFields?.gs1}
        productCode={productCode}
        tagNumber={element.value ? Mustache.render(element.value, data) : data.tagNumber}
        customFields={data.customProductFields}
      />
    );
  },
};

const renderElements = (layout, data, version = 1) => {
  if (version === 2) {
    return <PalletTagV2 layout={layout} data={data} />;
  }

  return layout?.map((element) => {
    const renderMethod = renderComponents[element.type];
    return renderMethod ? renderMethod(element, data) : null;
  });
};

function PalletTagV2({ layout, data }) {
  const html = Mustache.render(layout.html, data);
  const css = layout.css;

  const parsedContent = useMemo(
    () =>
      parse(html, {
        replace: (domNode) => {
          if ((domNode as Element).name === 'barcode') {
            const { lot, gtinCode, rawDate, productCode } = data ?? {};
            const attributes = (domNode as Element).attribs as {
              text?: string;
              value?: string;
              tagtype?: string;
              scale?: number;
              textsize?: number;
              inkspread?: number;
              barcodeheight?: number;
              width?: number;
              height?: number;
              rotation?: 'N' | 'R' | 'L' | 'I';
            };

            return (
              <Barcode
                width={attributes.width}
                height={attributes.height}
                marginTop="0px"
                scale={attributes.scale}
                textsize={attributes.textsize}
                inkspread={attributes.inkspread}
                barCodeHeight={attributes.barcodeheight}
                tagType={attributes.tagtype}
                textTemplate={attributes.text}
                rotate={attributes.rotation}
                lotNumber={lot}
                gtinCode={gtinCode}
                packDate={rawDate}
                productCode={productCode}
                customFields={data.customProductFields}
                productGS1={data.customProductFields?.gs1}
                tagNumber={attributes.value ? Mustache.render(attributes.value, data) : ''}
              />
            );
          }
        },
      }),
    [html, data]
  );

  return <ScopedStyleContainer css={css}>{parsedContent}</ScopedStyleContainer>;
}

export function PalletTag({
  template,
  id,
  data, // Pass the data object that contains the mustache template variables
}) {
  const layout = template?.layout;

  if (!layout) {
    return null;
  }

  return (
    <Card
      variant="outlined"
      sx={{
        p: template.padding ?? 1,
        mb: 2,
        width: template.width,
        height: template.height,
        contain: 'layout paint',
        '@media print': { mb: 0, border: 'none' },
      }}
      id={id}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {renderElements(layout, data, template?.version)}
      </Box>
    </Card>
  );
}
